import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useState, useContext, useMemo } from "react";
import { convertToCSV } from "@solsys/solsys-common";
import {
    OptionsMenu,
    Button,
    NotificationContext,
    ResponsiveTableView,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { FaPlus, FaCog, FaFileCsv } from "react-icons/fa";
import { withTimestampSortOptions, resetListSort } from "../../listSort";
import useApolloListData from "../../useApolloListData";

const gqlQuery = gql`
  query fsrList($fsr_list_request: fsr_list_request) {
    fsr(fsr_list_request: $fsr_list_request) {
      edges {
        id
        crane_id
        created_at
        updated_at
        crane {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const ticketCountsQuery = gql`
  query fsrTicketCounts($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      edges {
        id
        fsr_id
        name
      }
    }
  }
`;

const serviceCountsQuery = gql`
  query fsrServiceCounts($service_list_request: service_list_request) {
    service(service_list_request: $service_list_request) {
      edges {
        id
        fsr_id
        service_definition {
          name
        }
      }
    }
  }
`;

export const ListOptions = {
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    craneId: "craneId",
};

export default function FSRList({ id: entityId, action, ...props }) {
    const { addNotification } = useContext(NotificationContext);
    const isAdd = action === "add";

    const [requestState, setRequestState] = props.state;
    let { sortBy, sortDirection, craneId } = requestState;
    const setListOptions = (state) =>
        setRequestState({ ...requestState, ...state });
    const [optionSortBy, setOptionSortBy] = useState(sortBy);
    const [optionSortDirection, setOptionSortDirection] = useState(sortDirection);
    const [optionCraneId, setOptionCraneId] = useState(craneId);

    const applyOptions = () => {
        setListOptions({
            sortBy: optionSortBy,
            sortDirection: optionSortDirection,
            craneId: optionCraneId,
        });
    };

    const resetOptions = () => {
        const defaults = resetListSort();
        setOptionSortDirection(defaults.sortDirection);
        setOptionSortBy(defaults.sortBy);
        setOptionCraneId("");
    };

    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
    const [limit, setLimit] = useState(100);

    const sortByOptions = withTimestampSortOptions([
        { text: "ID", sortBy: "id" },
    ]);

    const sortDirectionOptions = [
        { text: "A to Z", sortDirection: "ASC" },
        { text: "Z to A", sortDirection: "DESC" },
    ];

    let variables = {
        fsr_list_request: {
            filters: [],
            pagination: { limit },
            sorting: [],
        },
    };

    if (sortBy && sortDirection) {
        variables.fsr_list_request.sorting.push({
            field: sortBy,
            direction: sortDirection,
        });
    }

    if (craneId) {
        variables.fsr_list_request.filters.push({
            field: "crane_id",
            operator: "=",
            value: String(craneId),
        });
    }

    const [csvQuery, { loading: csvLoading }] = useLazyQuery(gqlQuery, {
        fetchPolicy: "network-only",
    });
    const [refetch, { loading, data }] = useLazyQuery(gqlQuery, {
        variables,
        fetchPolicy: "network-only",
        pollInterval: 5000,
        onError: (error) => {
            console.error(error);
            addNotification("bad", "Something went wrong");
        },
    });
    const list = useApolloListData(data, (localData) => localData?.fsr?.edges);

    const fsrIds = useMemo(
        () => list.map((f) => f.id).filter(Boolean),
        [list]
    );

    const fsrIdInFilter = fsrIds.length
        ? `(${fsrIds.join(", ")})`
        : null;

    const { data: ticketCountData } = useQuery(ticketCountsQuery, {
        skip: !fsrIdInFilter,
        variables: {
            ticket_list_request: {
                filters: [
                    { field: "fsr_id", operator: "in", value: fsrIdInFilter },
                ],
                pagination: { limit: 10000 },
            },
        },
        fetchPolicy: "network-only",
    });

    const { data: serviceCountData } = useQuery(serviceCountsQuery, {
        skip: !fsrIdInFilter,
        variables: {
            service_list_request: {
                filters: [
                    { field: "fsr_id", operator: "in", value: fsrIdInFilter },
                ],
                pagination: { limit: 10000 },
            },
        },
        fetchPolicy: "network-only",
    });

    const countsByFsr = useMemo(() => {
        const map = {};
        fsrIds.forEach((fid) => {
            map[fid] = { tickets: 0, services: 0 };
        });
        (ticketCountData?.ticket?.edges ?? []).forEach((t) => {
            if (t.fsr_id && map[t.fsr_id]) map[t.fsr_id].tickets += 1;
        });
        (serviceCountData?.service?.edges ?? []).forEach((s) => {
            if (s.fsr_id && map[s.fsr_id]) map[s.fsr_id].services += 1;
        });
        return map;
    }, [fsrIds, ticketCountData, serviceCountData]);

    useEffect(() => {
        if (props.onStateChange) props.onStateChange(requestState);
        refetch({ variables });
    }, [requestState, limit]);

  const count = data?.fsr?.totalCount ?? 0;

    if (props.isMaster && props.autoSelect && !loading && !isAdd && !entityId) {
        props.onSelect(list.length ? list[0].id : 0);
    }

    return (
        <OptionsMenu isOpen={isFilterMenuOpen}>
            <OptionsMenu.Menu>
                <div className="options-tools">
                    <Button
                        onClick={() => {
                            applyOptions();
                            setIsFilterMenuOpen(false);
                        }}
                        loading={loading}
                    >
                        Apply
                    </Button>
                    <button onClick={resetOptions}>Reset</button>
                    <button onClick={() => setIsFilterMenuOpen(false)}>Close</button>
                </div>

                <div className="options-section">
                    <h3>Filters</h3>
                    <div className="option">
                        <label>Crane ID:</label>
                        <input
                            type="number"
                            value={optionCraneId}
                            onChange={(e) => setOptionCraneId(e.target.value)}
                            placeholder="Filter by Crane ID"
                        />
                    </div>
                </div>

                <div className="options-section">
                    <h3>Sorting</h3>
                    <div className="option">
                        <label>Sort By:</label>
                        <select
                            value={optionSortBy}
                            onChange={(e) => setOptionSortBy(e.target.value)}
                        >
                            {sortByOptions.map((option) => (
                                <option key={option.text} value={option.sortBy}>
                                    {option.text}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="option">
                        <label>Sort Order:</label>
                        <select
                            value={optionSortDirection}
                            onChange={(e) => setOptionSortDirection(e.target.value)}
                        >
                            {sortDirectionOptions.map((option) => (
                                <option key={option.text} value={option.sortDirection}>
                                    {option.text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="options-tools">
                    <Button
                        onClick={() => {
                            applyOptions();
                            setIsFilterMenuOpen(false);
                        }}
                        loading={loading}
                    >
                        Apply
                    </Button>
                    <button onClick={resetOptions}>Reset</button>
                    <button onClick={() => setIsFilterMenuOpen(false)}>Close</button>
                </div>
            </OptionsMenu.Menu>
            <OptionsMenu.Layout>
                <div className="page-header">
                    <span className="page-header-title">FSR</span>
                    <div className="page-header-options">
                        <Button
                            onClick={async () => {
                                const csvResponse = await csvQuery({
                                    variables: {
                                        fsr_list_request: {
                                            ...variables.fsr_list_request,
                                            pagination: { limit: 10000 },
                                        },
                                    },
                                });
                                const fsrs = csvResponse?.data?.fsr?.edges ?? [];
                                const csvData = convertToCSV(
                                    fsrs.map((entity) => ({
                                        ID: entity.id,
                                        Crane: entity.crane?.name || "—",
                                        Tickets: countsByFsr[entity.id]?.tickets ?? 0,
                                        Services: countsByFsr[entity.id]?.services ?? 0,
                                        Created: entity.created_at
                                            ? DateTime.fromISO(entity.created_at, { zone: "utc" })
                                                .toLocal()
                                                .toFormat("MMM d, yyyy")
                                            : "—",
                                    }))
                                );
                                const blob = new Blob([csvData], {
                                    type: "text/csv;charset=utf-8;",
                                });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = url;
                                link.setAttribute(
                                    "download",
                                    `fsr_${DateTime.now().toFormat("yyyy-MM-dd")}.csv`
                                );
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                            loading={csvLoading}
                        >
                            <FaFileCsv />
                        </Button>
                        {props.isMaster && (
                            <button onClick={() => props.onSelect(0)}>
                                <FaPlus />
                            </button>
                        )}
                        <button onClick={() => setIsFilterMenuOpen(true)}>
                            <FaCog />
                        </button>
                    </div>
                </div>

                <ResponsiveTableView
                    style={{ height: "100%" }}
                    loading={loading}
                    headers={[
                        { label: "ID" },
                        { label: "Crane" },
                        { label: "Tickets" },
                        { label: "Services" },
                        { label: "Created" },
                    ]}
                    data={list.map((entity) => {
                        const c = countsByFsr[entity.id] || { tickets: 0, services: 0 };
                        return {
                            id: entity.id,
                            ID: entity.id,
                            Crane: entity.crane?.name || "—",
                            Tickets: c.tickets > 0 ? String(c.tickets) : "—",
                            Services: c.services > 0 ? String(c.services) : "—",
                            Created: entity.created_at
                                ? DateTime.fromISO(entity.created_at, { zone: "utc" })
                                    .toLocal()
                                    .toFormat("MMM d, yyyy")
                                : "—",
                            selected: String(entity.id) === String(entityId) && !isAdd,
                        };
                    })}
                    identityColumnKey="ID"
                    onRowClick={(row) => {
                        props.onSelect(row.id, true);
                    }}
                    totalCount={count}
                    addMoreResults={() => {
                        setLimit(limit + 100);
                    }}
                />
            </OptionsMenu.Layout>
        </OptionsMenu>
    );
}
